import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LoginPage from "pages/login/login";
import AdminObjectsAndClientsPage from "pages/admin/objectsAndClients/objects/objects";
import AdminReferenceBooksStagesPage from "pages/admin/referenceBooks/stages/stages";
import AdminReferenceBooksStageGroupsPage from "pages/admin/referenceBooks/stageGroups/stageGroups";
import AdminReferenceBooksStageUnitsPage from "pages/admin/referenceBooks/stageUnits/stageUnits";
import AdminReferenceBooksCitizenshipsPage from "pages/admin/referenceBooks/citizenships/citizenships";
import AdminReferenceBooksSurfacesPage from "pages/admin/referenceBooks/surfaces/surfaces";
import AdminMastersPage from "pages/admin/masters/masters";
import AdminKnowledgeBasePage from "pages/admin/knowledgeBase/knowledgeBase";
import MasterSchedulePage from "pages/master/schedule/schedule";
import MasterFinancePage from "pages/master/finance/finance";
import MasterKnowledgeBasePage from "pages/master/knowledgeBase/knowledgeBase";
import MasterObjectStatisticsPage from "pages/master/objectStatistics/objectStatistics";
import SeniorMasterFinancePage from "pages/seniorMaster/finance/finance";
import SeniorMasterSchedulePage from "pages/seniorMaster/schedule/schedule";
import SeniorMasterVolumeConfirmationPage from "pages/seniorMaster/volumeConfirmation/volumeConfirmation";
import AccountantFinancePage from "pages/accountant/finance/finance";
import ProtectedRoute from "components/navigation/protectedRoute/protectedRoute";
import BaseLayout from "layouts/base/base";
import { CreateInterceptors } from "services/fetchIntercept/fetchIntercept";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import useUserStore from "stores/user";
import "./App.css";

const interceptors = CreateInterceptors.getInstance();
interceptors.registerInterceptors();

function App() {
  const { user, setUser } = useUserStore((store) => store);

  //  Check authorization
  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedDataJson = localStorage.getItem("userData");
        const storedData = storedDataJson ? JSON.parse(storedDataJson) : {};

        if (!storedData?.id) throw new Error();

        const url = `${apiDomain}/api/records/users/${storedData.id}`;
        const options: RequestInit = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        };

        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        const data = await response.json();

        //  Set user data to state
        setUser(() => {
          return {
            isAuthorized: true,
            isAuthorizationChecked: true,
            userData: data,
          };
        });

        //  Set user data to localStorage
        localStorage.setItem("userData", JSON.stringify(data));
      } catch (error) {
        logout();
      }
    };

    fetchData();
  }, [setUser]);

  return (
    <>
      <Router>
        <BaseLayout>
          <Routes>
            <Route
              path="/login"
              element={
                <ProtectedRoute element={<LoginPage />} isLoginPage={true} />
              }
            />
            <Route
              path="/reference-books/stages/*"
              element={
                <ProtectedRoute
                  roles={["admin"]}
                  element={<AdminReferenceBooksStagesPage />}
                />
              }
            />
            <Route
              path="/reference-books/stage-groups/*"
              element={
                <ProtectedRoute
                  roles={["admin"]}
                  element={<AdminReferenceBooksStageGroupsPage />}
                />
              }
            />
            <Route
              path="/reference-books/stage-units/*"
              element={
                <ProtectedRoute
                  roles={["admin"]}
                  element={<AdminReferenceBooksStageUnitsPage />}
                />
              }
            />
            <Route
              path="/reference-books/citizenships/*"
              element={
                <ProtectedRoute
                  roles={["admin"]}
                  element={<AdminReferenceBooksCitizenshipsPage />}
                />
              }
            />
            <Route
              path="/reference-books/surfaces/*"
              element={
                <ProtectedRoute
                  roles={["admin"]}
                  element={<AdminReferenceBooksSurfacesPage />}
                />
              }
            />
            <Route
              path="/objects-and-clients/*"
              element={
                <ProtectedRoute
                  roles={["admin"]}
                  element={<AdminObjectsAndClientsPage />}
                />
              }
            />
            <Route
              path="/masters/*"
              element={
                <ProtectedRoute
                  roles={["admin"]}
                  element={<AdminMastersPage />}
                />
              }
            />
            <Route
              path="/knowledge-base/*"
              element={
                <ProtectedRoute
                  roles={["admin", "master"]}
                  element={
                    user.userData.role === "admin" ? (
                      <AdminKnowledgeBasePage />
                    ) : (
                      <MasterKnowledgeBasePage />
                    )
                  }
                />
              }
            />
            <Route
              path="/schedule"
              element={
                <ProtectedRoute
                  roles={["master", "senior_master"]}
                  element={
                    user.userData.role === "master" ? (
                      <MasterSchedulePage />
                    ) : (
                      <SeniorMasterSchedulePage />
                    )
                  }
                />
              }
            />
            <Route
              path="/finance/*"
              element={
                <ProtectedRoute
                  roles={["master", "senior_master", "accountant"]}
                  element={
                    user.userData.role === "master" ? (
                      <MasterFinancePage />
                    ) : user.userData.role === "senior_master" ? (
                      <SeniorMasterFinancePage />
                    ) : (
                      <AccountantFinancePage />
                    )
                  }
                />
              }
            />
            <Route
              path="/volume-confirmation"
              element={
                <ProtectedRoute
                  roles={["senior_master"]}
                  element={<SeniorMasterVolumeConfirmationPage />}
                />
              }
            />
            <Route
              path="/object-statistics"
              element={
                <ProtectedRoute
                  roles={["master"]}
                  element={<MasterObjectStatisticsPage />}
                />
              }
            />
            <Route path="*" element={<ProtectedRoute />} />
          </Routes>
        </BaseLayout>
      </Router>
    </>
  );
}

export default App;
